/**
 * @module core/dataSource/utils
 * @description 数据源域纯函数：字段类型转换、表列映射、数值判定。
 */

import { isNil, isNumber, isString } from 'lodash-es';

import { FieldType } from './types';
import type { TFieldType } from './types';

const NUMERIC_PATTERN =
  /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i;

/** 后端 origin_type / 逻辑类型字符串 → TFieldType 映射。 */
const ORIGIN_TYPE_TO_FIELD_TYPE: Record<string, TFieldType> = {
  string: FieldType.String,
  bigint: FieldType.Number,
  decimal: FieldType.Number,
  date: FieldType.Date,
  datetime: FieldType.Date,
  int: FieldType.Number,
  float: FieldType.Number,
  double: FieldType.Number,
};

/**
 * 判断传入值是否为有限数值或合法数值字符串。
 *
 * @core AliTableAnalytics 排序、条件格式等数值分支判定。
 */
export const isNumericString = <T>(value: T): boolean => {
  if (isNil(value)) {
    return false;
  }

  if (isNumber(value)) {
    return Number.isFinite(value);
  }

  if (!isString(value)) {
    return false;
  }

  const trimmedValue = value.trim();
  if (trimmedValue === '') {
    return false;
  }

  if (!NUMERIC_PATTERN.test(trimmedValue)) {
    return false;
  }

  return Number.isFinite(Number(trimmedValue));
};

/**
 * 将后端 origin_type / column type 字符串转为 TFieldType。
 *
 * @core AB 实验配置转换、字段导入等。
 */
export const convertColumnType = (type: string): TFieldType => {
  return ORIGIN_TYPE_TO_FIELD_TYPE[type.toLowerCase()] ?? FieldType.String;
};
